import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

function ForgotPassword() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle', 'sending', 'success', 'error'
  const [message, setMessage] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [lockoutTime, setLockoutTime] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!email.trim()) {
      setStatus('error');
      setMessage('Please enter your email address');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('sending');
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setMessage(data.message);
        setRemainingAttempts(null);
        setLockoutTime(null);
      } else if (response.status === 429) {
        // Rate limited
        setStatus('error');
        setMessage(data.error || 'Too many attempts. Please try again later.');
        setLockoutTime(data.lockout_time);
      } else {
        setStatus('error');
        setMessage(data.error || 'Failed to send reset email. Please try again.');
        setRemainingAttempts(data.remaining_attempts);
      }
    } catch (error) {
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
      console.error('Forgot password error:', error);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>Reset Your Password</h1>
          <p style={styles.subtitle}>
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {status === 'success' ? (
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>✓</div>
            <p style={styles.successMessage}>{message}</p>
            <div style={styles.infoBox}>
              <p style={styles.infoText}>
                <strong>Next steps:</strong>
              </p>
              <ul style={styles.infoList}>
                <li>Check your email inbox (and spam folder)</li>
                <li>Click the password reset link in the email</li>
                <li>The link expires in 1 hour for security</li>
              </ul>
            </div>
            <button
              onClick={() => navigate('/?mode=doctor')}
              style={styles.backButton}
            >
              Back to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label htmlFor="email" style={styles.label}>
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="doctor@example.com"
                style={styles.input}
                disabled={status === 'sending'}
              />
            </div>

            {status === 'error' && (
              <div style={styles.errorBox}>
                <p style={styles.errorText}>{message}</p>
                {remainingAttempts !== null && (
                  <p style={styles.attemptsText}>
                    Remaining attempts: {remainingAttempts}
                  </p>
                )}
                {lockoutTime && (
                  <p style={styles.lockoutText}>
                    Please wait {Math.ceil(lockoutTime / 60)} minutes before trying again.
                  </p>
                )}
              </div>
            )}

            <button
              type="submit"
              style={{
                ...styles.submitButton,
                ...(status === 'sending' ? styles.submitButtonDisabled : {}),
              }}
              disabled={status === 'sending'}
            >
              {status === 'sending' ? 'Sending...' : 'Send Reset Link'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/?mode=doctor')}
              style={styles.cancelButton}
            >
              Back to Login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '12px',
  },
  subtitle: {
    fontSize: '15px',
    color: '#666',
    lineHeight: '1.6',
    margin: '0',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  inputGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.3s',
    boxSizing: 'border-box',
  },
  submitButton: {
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 24px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    marginBottom: '12px',
  },
  submitButtonDisabled: {
    backgroundColor: '#9ca3db',
    cursor: 'not-allowed',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: '#667eea',
    border: '2px solid #667eea',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  backButton: {
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    marginTop: '20px',
  },
  successContainer: {
    textAlign: 'center',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    margin: '0 auto 20px',
    backgroundColor: '#10b981',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '48px',
    color: 'white',
    fontWeight: 'bold',
  },
  successMessage: {
    fontSize: '16px',
    color: '#333',
    marginBottom: '20px',
    lineHeight: '1.6',
  },
  infoBox: {
    backgroundColor: '#f0f9ff',
    borderLeft: '4px solid #667eea',
    padding: '16px',
    borderRadius: '4px',
    marginTop: '20px',
    textAlign: 'left',
  },
  infoText: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '8px',
  },
  infoList: {
    fontSize: '14px',
    color: '#666',
    paddingLeft: '20px',
    margin: '0',
  },
  errorBox: {
    backgroundColor: '#fef2f2',
    borderLeft: '4px solid #ef4444',
    padding: '16px',
    borderRadius: '4px',
    marginBottom: '20px',
  },
  errorText: {
    fontSize: '14px',
    color: '#991b1b',
    margin: '0 0 8px 0',
    fontWeight: '500',
  },
  attemptsText: {
    fontSize: '13px',
    color: '#dc2626',
    margin: '4px 0',
    fontWeight: '600',
  },
  lockoutText: {
    fontSize: '13px',
    color: '#dc2626',
    margin: '4px 0',
    fontWeight: '600',
  },
};

export default ForgotPassword;
